import along from "@turf/along";
import length from "@turf/length";
import { convertLength } from "@turf/helpers";
import { Units as StyleUnits } from "@iventis/domain-model/model/units";
import { AnySupportedGeometry, MapObjectProperties } from "@iventis/map-types";
import { createLog } from "@iventis/observability-and-monitoring";
import GeoJSON from "geojson";

/** Maximum number of distance markers generated for one map object. */
export const MAX_DISTANCE_MARKERS_PER_MAP_OBJECT = 1000;

/** Feature property containing the formatted marker distance. */
export const DISTANCE_MARKER_LABEL_PROPERTY = "distanceMarkerLabel";

/** Feature property containing the marker distance in metres. */
export const DISTANCE_MARKER_DISTANCE_PROPERTY = "distanceMarkerDistance";

export type DistanceMarkerPlacement = "center" | "interval";

/** Formats a distance in metres for display in the configured unit. */
export function formatDistanceMarkerLabel(distanceMeters: number, unit: StyleUnits) {
    switch (unit) {
        case StyleUnits.Meters:
            return `${Math.round(distanceMeters * 100) / 100} m`;
        case StyleUnits.Kilometers:
            return `${Math.round(convertLength(distanceMeters, "meters", "kilometers") * 100) / 100} km`;
        case StyleUnits.Miles:
            return `${Math.round(convertLength(distanceMeters, "meters", "miles") * 100) / 100} mi`;
        case StyleUnits.Feet:
            return `${Math.round(convertLength(distanceMeters, "meters", "feet") * 100) / 100} ft`;
        default:
            throw new Error(`Unsupported distance marker unit: ${unit}`);
    }
}

/**
 * Converts a configured marker interval to metres.
 *
 * @param interval The configured marker interval.
 * @param unit The configured marker interval unit.
 * @returns The marker interval in metres, or 0 if the configured interval is non-positive or non-finite.
 */
export function distanceMarkerIntervalToMeters(interval: number, unit: StyleUnits) {
    if (!Number.isFinite(interval) || interval <= 0) {
        return 0;
    }

    switch (unit) {
        case StyleUnits.Meters:
            return interval;
        case StyleUnits.Kilometers:
            return convertLength(interval, "kilometers", "meters");
        case StyleUnits.Miles:
            return convertLength(interval, "miles", "meters");
        case StyleUnits.Feet:
            return convertLength(interval, "feet", "meters");
        default:
            throw new Error(`Unsupported distance marker unit: ${unit}`);
    }
}

/**
 * Creates labelled point features at the start and each configured interval along every valid line feature.
 * Marker generation is capped per input feature to protect map rendering from very small intervals.
 *
 * @param features The line features to generate markers for.
 * @param interval The distance between markers in the configured unit.
 * @param unit The unit of the configured interval.
 * @param excludedObjectIds Map object IDs whose markers should not be generated.
 * @returns A flat array of point features with the same properties as the input line features, plus a `distanceMarkerLabel` and `distanceMarkerDistance` property.
 */
export function createLineDistanceMarkerPoints(
    features: GeoJSON.Feature<AnySupportedGeometry, MapObjectProperties>[],
    interval: number,
    unit: StyleUnits,
    excludedObjectIds: string[] = [],
    placement: DistanceMarkerPlacement = "interval"
): GeoJSON.Feature<GeoJSON.Point, MapObjectProperties>[] {
    const intervalMeters = distanceMarkerIntervalToMeters(interval, unit);
    if (intervalMeters <= 0) {
        return [];
    }

    return features.flatMap((lineFeature) => {
        if (excludedObjectIds.includes(lineFeature.properties.id)) {
            return [];
        }

        if (lineFeature.geometry?.type !== "LineString" || lineFeature.geometry.coordinates.length < 2) {
            return [];
        }

        const lineDistanceMeters = length(lineFeature.geometry, { units: "meters" });
        if (!Number.isFinite(lineDistanceMeters)) {
            return [];
        }

        const potentialMarkerCount = Math.floor(lineDistanceMeters / intervalMeters) + 1;
        if (potentialMarkerCount > MAX_DISTANCE_MARKERS_PER_MAP_OBJECT) {
            createLog("Line distance markers exceeded maxDistanceMarkersPerMapObject", "info", {
                potentialMarkerCount,
                maxDistanceMarkersPerMapObject: MAX_DISTANCE_MARKERS_PER_MAP_OBJECT,
                intervalMeters,
                lineDistanceMeters,
            });
        }

        const pointFeatures: GeoJSON.Feature<GeoJSON.Point, MapObjectProperties>[] = [];

        const createMarker = (markerDistanceMeters: number, labelDistanceMeters = markerDistanceMeters) => {
            const markerPoint = along(lineFeature.geometry, markerDistanceMeters, { units: "meters" });
            return {
                ...markerPoint,
                properties: {
                    ...lineFeature.properties,
                    [DISTANCE_MARKER_DISTANCE_PROPERTY]: markerDistanceMeters,
                    [DISTANCE_MARKER_LABEL_PROPERTY]: formatDistanceMarkerLabel(labelDistanceMeters, unit),
                },
            };
        };

        if (placement === "center") {
            pointFeatures.push(createMarker(lineDistanceMeters / 2, intervalMeters));
            return pointFeatures;
        }

        for (let markerIndex = 0; markerIndex < MAX_DISTANCE_MARKERS_PER_MAP_OBJECT; markerIndex += 1) {
            const markerDistanceMeters = markerIndex * intervalMeters;
            if (markerDistanceMeters > lineDistanceMeters) {
                break;
            }

            pointFeatures.push(createMarker(markerDistanceMeters));
        }

        return pointFeatures;
    });
}
